import React, { createContext, FC, useContext, useState } from "react";
import { AxiosResponse } from "axios";
import {
  AppleLinkEmailInput,
  AppleLinkEmailOutput,
  VerifyEmailInput,
  TwoFactorAuthMethod,
} from "@ifgengineering/client-auth-sdk";
import {
  TwoFactorAuthDetailsInput,
  TwoFactorAuthDetailsOutput,
} from "@ifgengineering/client-auth-sdk/lib/types";
export interface IAuthComponentsContext {
  children?: React.ReactNode;
  charity?: any;
  isCur8InWealthEnabled: boolean;
  isLayoutBEnabled: boolean;
  isMagicCodeEnabled: boolean;
  isTurnstileEnabled?: boolean;
  is2FAEnabled?: boolean;
  is2FAFlow?: boolean;
  setIs2FAFlow: (is2FAFlow: boolean) => void;
  logException: ({
    email,
    tag,
    exception,
  }: {
    email?: string;
    tag?: string;
    exception: string;
  }) => void;
  loginAction: any;
  /**
   * @deprecated reCAPTCHA is being replaced by Turnstile. Use turnstileSiteKey instead.
   * This field will be removed in a future release.
   */
  recaptchaClientKey?: string;
  turnstileSiteKey: string;
  resetPassword: ({
    email,
    newPassword,
    token,
  }: {
    email: string;
    newPassword: string;
    token: string;
  }) => Promise<void>;
  sendResetPasswordLink: (email: string) => Promise<
    AxiosResponse<{
      email: string;
      origin: string;
    }>
  >;
  signupAction: any;
  useAnalytics: any;
  verifyEmail: (payload: VerifyEmailInput) => Promise<void>;
  passwordlessStart: (
    email: string,
    isSignup?: boolean | undefined
  ) => Promise<void>;
  passwordlessVerify: (
    email: string,
    otp: string,
    isSignup?: boolean | undefined
  ) => Promise<void>;
  setAppleAuthData: (data: IAppleAuthData) => void;
  appleAuthData?: IAppleAuthData;
  generateOTP: (email: string, method?: TwoFactorAuthMethod) => Promise<void>;
  getTwoFactorAuthDetails?: (
    payload: TwoFactorAuthDetailsInput
  ) => Promise<TwoFactorAuthDetailsOutput>;
  appleLinkEmail: (
    data: AppleLinkEmailInput
  ) => Promise<AxiosResponse<AppleLinkEmailOutput>>;
  emailPassword: {
    email: string;
    password: string;
  };
  setEmailPassword: (data: { email: string; password: string }) => void;
}

interface IAppleAuthData {
  isPrivateEmail?: boolean;
  authorizationCode?: string;
  linkEmail?: string;
  linkOtp?: string;
}

export const AuthComponentsContext = createContext(
  {} as IAuthComponentsContext
);

export const AuthComponentsProvider: FC<IAuthComponentsContext> = ({
  charity,
  children,
  isCur8InWealthEnabled,
  isLayoutBEnabled,
  isMagicCodeEnabled,
  isTurnstileEnabled,
  logException,
  loginAction,
  recaptchaClientKey,
  turnstileSiteKey,
  resetPassword,
  sendResetPasswordLink,
  signupAction,
  useAnalytics,
  verifyEmail,
  passwordlessStart,
  passwordlessVerify,
  generateOTP,
  getTwoFactorAuthDetails,
  appleLinkEmail,
  is2FAEnabled,
}) => {
  const [appleAuthData, setAppleAuthData] = useState<IAppleAuthData>({
    isPrivateEmail: false,
  });

  const [is2FAFlow, setIs2FAFlow] = useState(false);
  const [emailPassword, setEmailPassword] = useState({
    email: "",
    password: "",
  });

  return (
    <AuthComponentsContext.Provider
      value={{
        charity,
        isCur8InWealthEnabled,
        isLayoutBEnabled,
        isMagicCodeEnabled,
        isTurnstileEnabled,
        is2FAEnabled,
        is2FAFlow,
        setIs2FAFlow,
        logException,
        loginAction,
        recaptchaClientKey,
        turnstileSiteKey,
        resetPassword,
        sendResetPasswordLink,
        signupAction,
        useAnalytics,
        verifyEmail,
        passwordlessStart,
        passwordlessVerify,
        generateOTP,
        getTwoFactorAuthDetails,
        appleLinkEmail,
        appleAuthData,
        setAppleAuthData: (data) => {
          setAppleAuthData((prevState) => ({ ...prevState, ...data }));
        },
        emailPassword,
        setEmailPassword,
      }}
    >
      {children}
    </AuthComponentsContext.Provider>
  );
};

export const useClientAuthComponents = () => useContext(AuthComponentsContext);
